import { PageContainer } from '@/components/common/page-container';
import { PageHeader } from '@/components/common/page-header';
import { useMisPaquetes } from '@/lib/hooks/use-paquetes';
import { PinDisplay } from '@/components/paquetes/pin-display';
import { EstadoPaqueteBadge } from '@/components/paquetes/estado-badge';
import type { Paquete } from '@condo/contracts';

function fmt(iso: string): string {
  return new Date(iso).toLocaleDateString('es-CL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function PaquetesResidenteView() {
  const { data, isLoading } = useMisPaquetes();

  const pendientes = (data ?? []).filter((p) => p.estado === 'recibido');
  const historial = (data ?? []).filter((p) => p.estado !== 'recibido');
  const sorted: Paquete[] = [...pendientes, ...historial];

  return (
    <PageContainer width="wide">
      <PageHeader eyebrow="Encomiendas" title="Mis paquetes" />

      {isLoading && (
        <div className="py-16 text-center text-sm text-[var(--color-ink-muted)]">
          Cargando paquetes…
        </div>
      )}

      {!isLoading && sorted.length === 0 && (
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <span className="text-4xl">📦</span>
          <p className="text-sm text-[var(--color-ink-muted)]">
            No tienes paquetes registrados.
          </p>
        </div>
      )}

      {!isLoading && sorted.length > 0 && (
        <div className="flex flex-col gap-4">
          {pendientes.length > 0 && (
            <p className="text-xs font-semibold uppercase tracking-widest text-[var(--color-ink-muted)]">
              Pendientes de retiro ({pendientes.length})
            </p>
          )}

          {sorted.map((p) => {
            const isPending = p.estado === 'recibido';

            return (
              <div
                key={p.id}
                className={[
                  'rounded-xl border p-5 transition-shadow',
                  isPending
                    ? 'border-[var(--color-accent)] bg-[#FFFDF5] shadow-sm'
                    : 'border-[var(--color-hairline)] bg-[var(--color-paper)] opacity-70',
                ].join(' ')}
              >
                {/* Header row */}
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-sm truncate">
                      {p.descripcion ?? 'Paquete sin descripción'}
                    </p>
                    {p.remitente && (
                      <p className="text-xs text-[var(--color-ink-muted)] mt-0.5 truncate">
                        De: {p.remitente}
                      </p>
                    )}
                    {p.trackingNumber && (
                      <p className="font-mono text-xs text-[var(--color-ink-muted)] mt-0.5 truncate">
                        Tracking: {p.trackingNumber}
                      </p>
                    )}
                    <p className="text-xs text-[var(--color-ink-muted)] mt-1">
                      Recibido el {fmt(p.registradoAt)}
                      {p.retiradoAt && ` · Retirado el ${fmt(p.retiradoAt)}`}
                      {p.devueltoAt && ` · Devuelto el ${fmt(p.devueltoAt)}`}
                      {p.anuladoAt && ` · Anulado el ${fmt(p.anuladoAt)}`}
                    </p>
                  </div>

                  <EstadoPaqueteBadge estado={p.estado} />
                </div>

                {/* PIN block — only for pending paquetes */}
                {isPending && <PinDisplay pin={p.pin} />}
              </div>
            );
          })}

          {historial.length > 0 && (
            <p className="text-xs text-[var(--color-ink-muted)] text-center pt-2">
              {historial.length} paquete{historial.length !== 1 ? 's' : ''} en historial
            </p>
          )}
        </div>
      )}
    </PageContainer>
  );
}
